import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 通行禁止区域設定削除 API
 */
export const useDeleteNoEntryZoneSettingAPI = (): {
  state: APIState;
  deleteNoEntryZoneSetting: (noEntryZoneSettingId: string) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const request = useCallback(
    async (
      projectId: string,
      environmentId: string,
      noEntryZoneSettingId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.delete(
          `/${projectId}/environments/${environmentId}/no_entry_zone_settings/${noEntryZoneSettingId}`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );
  const deleteNoEntryZoneSetting = useAtomCallback(
    useCallback(
      async (get, _, noEntryZoneSettingId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.no_entry_zone_setting.delete', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await request(
          project.id,
          environment.environment_id,
          noEntryZoneSettingId,
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.no_entry_zone_setting.delete', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t('api.fms.no_entry_zone_setting.delete', {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return res.value;
      },
      [notifyError, notifySuccess, request, t, getErrorMessage],
    ),
  );

  return {
    state,
    deleteNoEntryZoneSetting,
  };
};
